// Shared utility functions (safe for both client and server)

export type VerificationResult = 'VALID' | 'EXPIRED' | 'NOT_ACTIVE' | 'NOT_FOUND';

export interface VerificationResponse {
  result: VerificationResult;
  license?: any;
  verificationTimestamp: string;
  verificationReference: string;
}

// ── Standard license validity constants ─────────────────────────────────────
// Default dates applied to all licenses (can be overridden per license via admin edit)
export const JKDM_ISSUE_DATE  = '2026-03-19'; // 19 March 2026
export const JKDM_VALID_FROM  = '2026-03-20'; // 20 March 2026
export const JKDM_VALID_UNTIL = '2027-03-19'; // 19 March 2027

/**
 * Returns true if the given organization code or customer name belongs to JKDM.
 */
export function isJKDMLicense(organizationCode?: string, customerName?: string): boolean {
  const orgMatch  = (organizationCode ?? '').trim().toUpperCase() === 'JKDM';
  const nameMatch = (customerName ?? '').trim().toLowerCase().includes('jabatan kastam diraja malaysia');
  return orgMatch || nameMatch;
}

/**
 * Returns the correct valid_from / valid_until for a license.
 * For JKDM licenses the dates are always fixed to the official 1-year period.
 */
export function getValidityDates(
  organizationCode?: string,
  customerName?: string,
  defaultFrom?: string,
  defaultUntil?: string
): { valid_from: string; valid_until: string } {
  if (isJKDMLicense(organizationCode, customerName)) {
    return { valid_from: JKDM_VALID_FROM, valid_until: JKDM_VALID_UNTIL };
  }
  return { valid_from: defaultFrom ?? '', valid_until: defaultUntil ?? '' };
}

export function generateLicenseNumber(params: {
  org: string;
  module: string;
  type: string;
  entitlement: string;
  issueDate: string;
  seq: string;
}): string {
  const { org, module, type, entitlement, issueDate, seq } = params;
  if (!org || !module || !type || !entitlement || !issueDate || !seq) return '';
  const datePart = issueDate.replace(/-/g, '').slice(2); // YYMMDD
  return `${org}-${module}-${type}-${entitlement}-${datePart}-${seq}`;
}

export function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function getDaysUntilExpiry(validUntil: string): number {
  const now = new Date();
  const expiry = new Date(validUntil);
  const diff = expiry.getTime() - now.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}